// Enhanced Google Analytics tracking utilities

// Extend Window interface for dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// UTM parameter names
const UTM_PARAMETERS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// Track page views with enhanced data
export const trackPageView = (additionalData: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'pageview',
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_search: window.location.search,
      ...additionalData
    });
  }
};

// Capture and track UTM parameters
export const trackUTMParameters = () => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    const urlParams = new URLSearchParams(window.location.search);
    const utmData: Record<string, string> = {};
    let hasUTM = false;

    // Collect all UTM parameters
    UTM_PARAMETERS.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmData[param] = value;
        hasUTM = true;
      }
    });

    // Only track if UTM parameters exist
    if (hasUTM) {
      window.dataLayer.push({
        event: 'utm_tracking',
        ...utmData,
        page_location: window.location.href,
        page_path: window.location.pathname
      });
      
      console.log('UTM parameters tracked:', utmData);
    }
  }
};

// Track link clicks
export const trackLinkClick = (element: HTMLElement, event: MouseEvent) => {
  if (typeof window !== 'undefined' && window.dataLayer && element instanceof HTMLAnchorElement) {
    const href = element.href;
    const linkText = element.textContent?.trim() || element.innerText?.trim() || '';
    const linkTitle = element.title || '';
    
    // Determine if it's an external link
    const isExternal = href && !href.startsWith(window.location.origin) && !href.startsWith('/') && !href.startsWith('#');
    
    // Get additional context
    const linkData: Record<string, any> = {
      event: 'link_click',
      link_url: href,
      link_text: linkText,
      link_title: linkTitle,
      link_domain: href ? new URL(href).hostname : '',
      link_type: isExternal ? 'external' : 'internal',
      click_element: element.tagName.toLowerCase(),
      page_location: window.location.href,
      page_path: window.location.pathname
    };

    // Add UTM context if present in current URL
    const urlParams = new URLSearchParams(window.location.search);
    UTM_PARAMETERS.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        linkData[`source_${param}`] = value;
      }
    });

    window.dataLayer.push(linkData);
    console.log('Link click tracked:', linkData);
  }
};

// Initialize link tracking
export const initializeLinkTracking = () => {
  if (typeof window !== 'undefined') {
    // Use event delegation to catch all link clicks, including dynamically added ones
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      // Find the closest anchor element (handles clicks on children of links)
      const linkElement = target.closest('a');
      
      if (linkElement) {
        const href = linkElement.href;
        
        // Check if it's a file download
        const fileExtension = href ? href.split('.').pop()?.toLowerCase() : '';
        const downloadableExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'csv', 'txt'];
        
        if (fileExtension && downloadableExtensions.includes(fileExtension)) {
          // Track as download
          const fileName = href.split('/').pop() || 'unknown';
          trackDownload(fileName, fileExtension);
        }
        
        // Track as regular link click
        trackLinkClick(linkElement, event);
      }
    }, true); // Use capture phase to ensure we catch the event
    
    console.log('Link tracking initialized');
  }
};

// Track search queries (if search functionality exists)
export const trackSearch = (query: string, results?: number, category?: string) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'search',
      search_term: query,
      search_results: results,
      search_category: category,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
    
    console.log('Search tracked:', { query, results, category });
  }
};

// Track document downloads (for PDF, DOC, etc.)
export const trackDownload = (fileName: string, fileType: string, fileSize?: number) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'file_download',
      file_name: fileName,
      file_type: fileType,
      file_size: fileSize,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
    
    console.log('Download tracked:', { fileName, fileType, fileSize });
  }
};

// Track custom events with UTM context
export const trackCustomEvent = (eventName: string, eventData: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    const data: Record<string, any> = {
      event: eventName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...eventData
    };

    // Add UTM context if present
    const urlParams = new URLSearchParams(window.location.search);
    UTM_PARAMETERS.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        data[`context_${param}`] = value;
      }
    });

    window.dataLayer.push(data);
    console.log('Custom event tracked:', data);
  }
};

// Enhanced login tracking with UTM context
export const trackLoginEvent = (method: string, userEmail: string, success: boolean = true) => {
  trackCustomEvent(success ? 'login' : 'login_failed', {
    login_method: method,
    user_email: userEmail,
    eventProps: {
      category: 'login',
      action: `login with ${method}`,
      label: `user: ${userEmail}`,
      value: success ? 1 : 0,
    }
  });
};

// Enhanced logout tracking
export const trackLogoutEvent = (userEmail?: string, reason?: string) => {
  trackCustomEvent('logout', {
    user_email: userEmail,
    logout_reason: reason,
    eventProps: {
      category: 'logout',
      action: 'user logout',
      label: userEmail ? `user: ${userEmail}` : 'unknown user',
      value: 1,
    }
  });
};